import React, {MouseEvent, useCallback, useState} from 'react';
import {toast} from 'react-toastify';
import {useDispatch, useSelector} from 'react-redux';
import cn from 'classnames';
import {t} from '@lingui/core/macro';

import {additionalEmailAPI, IAdditionalEmail} from 'entities/notification';
import {userAPI, EmailErrorType, setUserEmailActive, userSelector} from 'entities/user';
import StatusToggle from 'shared/ui/StatusToggle/StatusToggle';
import NotifyButton from 'shared/ui/Button/NotifyButton/NotifyButton';
import Problem from 'shared/ui/icons/General/Problem';
import Wrong from 'shared/ui/icons/General/Wrong';
import {TypedDispatch} from 'shared/model/types';
import useTestNotify from 'features/profile/model/useTestNotify';

import {PALETTE} from 'shared/config/model/palette';

import {ActionCells, DescriptionCell} from '../NotifyChannel/NotifyChannelCells';
import NotifyChannelStatusIcon from '../NotifyChannel/NotifyChannelStatusIcon';
import NotifyChannelEditModal from '../NotifyChannel/NotifyChannelEditModal';
import NotifyChannelDeleteModal from '../NotifyChannel/NotifyChannelDeleteModal/NotifyChannelDeleteModal';

import styles from '../NotifyChannel/NotifyChannels.module.scss';

interface IAdditionalEmailRowProps {
    item: IAdditionalEmail
}

const AdditionalEmailRow: React.FC<IAdditionalEmailRowProps> = ({item}) => {
    const dispatch = useDispatch<TypedDispatch>();
    const user = useSelector(userSelector);
    const [isModalDeleteOpen, setIsModalDeleteOpen] = useState(false);
    const [isModalEditOpen, setIsModalEditOpen] = useState(false);
    const serverActive = item.is_main ? user?.email_active ?? false : item.active;
    const [localActive, setLocalActive] = useState(serverActive);
    const [syncedActive, setSyncedActive] = useState(serverActive);

    if (syncedActive !== serverActive) {
        setSyncedActive(serverActive);
        setLocalActive(serverActive);
    }

    const EMAIL_ERROR_DESCRIPTIONS_EMAIL: Record<EmailErrorType, string> = {
        invalid: t`Адрес недействителен или содержит ошибки`,
        permanent_unavailable: t`Адрес недоступен`,
        blocked: t`Адрес заблокирован`,
        complained: t`Адрес заблокирован`,
        unsubscribed: t`Вы отписались от рассылок на этот адреc`,
        temporary_unavailable: t`Адрес временно недоступен`,
    };

    const [apiUserToggleActiveEmail] = userAPI.useToggleActiveUserEmailMutation();
    const [apiUserSendEmailTestNotify] = userAPI.useSendEmailTestNotifyMutation();
    const [updateAdditionalEmail] = additionalEmailAPI.useUpdateMutation();
    const [apiSendTestNotify] = additionalEmailAPI.useSendTestNotifyMutation();

    const testNotify = useTestNotify(localActive, () => (
        item.is_main ? apiUserSendEmailTestNotify(user?.id ?? 0) : apiSendTestNotify(item.id)
    ));

    const handleToggle = useCallback(async(e: MouseEvent) => {
        e.preventDefault();
        const newActive = !localActive;
        setLocalActive(newActive);

        const result = item.is_main
            ? await apiUserToggleActiveEmail(user?.id ?? 0)
            : await updateAdditionalEmail({id: item.id, active: newActive});

        if ('error' in result) {
            setLocalActive(prev => !prev);
            return;
        }
        if (item.is_main) {
            dispatch(setUserEmailActive(newActive));
        }
        toast.info(newActive ? `${t`E-mail активирован`}` : `${t`E-mail деактивирован`}`);
    }, [localActive, item.is_main, item.id, user?.id, apiUserToggleActiveEmail, updateAdditionalEmail, dispatch]);

    const isMainEmail = item.is_main;
    const emailError = isMainEmail ? user?.last_email_error : item.last_email_error;
    const isConfirmed = isMainEmail ? user?.email_confirmed : true;

    const isProblem = !!emailError;
    const isWarning = !isConfirmed;
    const isNormal = !isProblem && !isWarning;

    const openEdit = isMainEmail ? undefined : () => setIsModalEditOpen(true);
    const openDelete = isMainEmail ? undefined : () => setIsModalDeleteOpen(true);

    return (
        <tr className={styles.row}>
            <td>
                {isNormal ? (
                    <div className={styles.containerTitle}>
                        <NotifyChannelStatusIcon active={localActive}/>
                        {item.email}
                    </div>
                ) : (
                    <>
                        <div className={styles.containerTitle}>
                            {(isProblem) && <Problem color={PALETTE.warningDark} />}
                            {isWarning && !isProblem && <Wrong />}
                            {item.email}
                        </div>
                        <div className={styles.containerSubTitle}>
                            <span
                                className={cn(
                                    styles.circle,
                                    isWarning && styles.warning,
                                    isProblem && styles.problem,
                                )}
                            />
                            <span className={styles.text}>
                                {isWarning && !isProblem && t`Адрес не подтверждён`}
                                {isProblem && emailError && (
                                    EMAIL_ERROR_DESCRIPTIONS_EMAIL[emailError as EmailErrorType] ||
                                    t`Неизвестная ошибка`
                                )}
                            </span>
                        </div>
                    </>
                )}
            </td>
            <DescriptionCell
                description={item.description}
                placeholder={isMainEmail ? t`Основной E-mail` : t`Не заполнено`}
                onEdit={openEdit}
            />
            <td>
                <StatusToggle
                    condition={localActive}
                    onClick={handleToggle}
                    disabled={emailError !== null && !localActive}
                />
            </td>
            <td>
                <NotifyButton
                    loading={testNotify.sending}
                    disabled={testNotify.disabled}
                    onClick={testNotify.onSend}
                />
            </td>
            <ActionCells onEdit={openEdit} onDelete={openDelete}/>
            <NotifyChannelEditModal
                item={item}
                api={additionalEmailAPI}
                open={isModalEditOpen}
                handleClose={() => setIsModalEditOpen(false)}
                header={t`Редактирование E-mail`}
                successText={t`Описание дополнительного email успешно обновлено`}
            />
            <NotifyChannelDeleteModal
                item={item}
                api={additionalEmailAPI}
                open={isModalDeleteOpen}
                handleClose={() => setIsModalDeleteOpen(false)}
                header={t`Удаление E-mail`}
                question={t`Вы уверены, что хотите удалить E-mail`}
                name={item.email}
                successText={t`E-mail удален`}
            />
        </tr>
    );
};

export default React.memo(AdditionalEmailRow);
